import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { onChangeFilterWithLogging } from "@shared/utils";

/**
 *
 * @param initialState Initial state of the url param
 * @param paramsName Name of the url param
 * @param serialize Function to properly serialize the state
 * @param deserialize Function to deserialize the state from the url param
 * @param logFromPage page from which the function is used. For fullstory logging purposes only (optional)
 * @returns
 */
export const useStateParams = <T,>(
  initialState: T,
  paramsName: string,
  serialize: (state: T) => string,
  deserialize: (state: string) => T,
  logFromPage?: string
): [T, (state: T) => void] => {
  const history = useHistory();
  const search = new URLSearchParams(history.location.search);

  const existingValue = search.get(paramsName);
  const [state, setState] = useState<T>(
    existingValue ? deserialize(existingValue) : initialState
  );

  const pushHistory = useCallback(
    (search: URLSearchParams) => {
      history.push({
        pathname: history.location.pathname,
        search: search.toString(),
      });
    },
    [history]
  );

  const deleteParams = useCallback(
    (search: URLSearchParams) => {
      if (search.has(paramsName)) {
        search.delete(paramsName);
        pushHistory(search);
      }
    },
    [paramsName, pushHistory]
  );

  useEffect(() => {
    // Updates state when user navigates backwards or forwards in browser history
    if (existingValue) {
      if (deserialize(existingValue) !== state) {
        setState(deserialize(existingValue));
      }
    } else {
      // Handle the case where someone manually sets the url to something like ?severity=&status=
      deleteParams(search);

      // Reset the param to its original state. This might get finnicky in the future,
      // but right now the initial state for most things should be either [] or ""
      if (state) {
        setState(initialState);
      }
    }
    // eslint-disable-next-line
  }, [existingValue]);

  const onChange = useCallback(
    (s: T) => {
      const searchParams = new URLSearchParams(history.location.search);
      if (!s || (Array.isArray(s) && s.length === 0)) {
        deleteParams(searchParams);
      } else {
        searchParams.set(paramsName, serialize(s));
        pushHistory(searchParams);
      }
      setState(s);

      // When logFromPage is specified, log the custom event to Fullstory
      if (logFromPage) {
        onChangeFilterWithLogging(paramsName, logFromPage);
      }
    },
    [
      deleteParams,
      history.location.search,
      paramsName,
      pushHistory,
      serialize,
      logFromPage,
    ]
  );

  return useMemo(() => [state, onChange], [state, onChange]);
};
